import { inject, Injectable } from '@angular/core';
import { NewCustomRouteService } from 'src/app/pages/new-custom-route/new-custom-route.service';
import { DataChatService } from './dataChat.service';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })

export class AssistantToolsHandlerService {
  private router = inject(Router);
  private dataChat = inject(DataChatService);
  private customRouteService = inject(NewCustomRouteService);
    
// POI Buffer per ID
  private partialActionBuffers = new Map<string, string>();

  public processAssistantTools(id: string, text: string): string {
    let buffer = (this.partialActionBuffers.get(id) || "") + text;
    let outputText = "";

    // DEBUG: Log entrada
    if (text.includes('ACTION')) {
      console.log('🔍 [TextAnim] processTextForActions entrada:', { id, textLength: text.length, text: text.substring(0, 200) });
      console.log('🔍 [TextAnim] Buffer completo:', buffer.substring(0, 500));
    }

    // 1. Process Complete Actions - WEATHER PRIMERO (tiene estructura más compleja)
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_WEATHER:', (jsonData) => {
      this.handleWeatherAction(id, jsonData);
    });

    // 2. Luego POI
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_POI:', (jsonData) => {
      console.log('🚀 [TextAnim] POI Action JSON data:', jsonData);
      this.handleNavigateToPoiAction(jsonData);
    });

    // 3. Fallback para WEB (cuando no hay POI oficial)
    buffer = this.extractAndHandleActionTag(id, buffer, '[ACTION:SHOW_WEB:', (jsonData) => {
      return;
      console.log('🚀 [TextAnim] WEB Action JSON data:', jsonData);
      this.handleExternalUrlAction(jsonData);
    });

    // 3. Check for partials at end
    const lastOpen = buffer.lastIndexOf('[');
    if (lastOpen !== -1) {
      const potentialTag = buffer.substring(lastOpen);
      // Si parece que empieza una acción, esperamos al siguiente chunk para cerrarla
      if (potentialTag.startsWith("[ACTION:")) {
        outputText = buffer.substring(0, lastOpen);
        this.partialActionBuffers.set(id, potentialTag);
        // Limpieza final de residuos
        return this.cleanActionResidues(outputText);
      }
    }

    // No hay tag parcial, flush
    this.partialActionBuffers.set(id, "");
    // Limpieza final de residuos
    return this.cleanActionResidues(buffer);
  }

  /** Extrae y maneja un action tag usando balance de llaves para JSON correcto */
  private extractAndHandleActionTag(id: string, buffer: string, prefix: string, handler: (jsonData: any) => void): string {
    let startIdx = buffer.indexOf(prefix);

    while (startIdx !== -1) {
      const jsonStart = startIdx + prefix.length;

      // Encontrar el JSON completo balanceando llaves
      let depth = 0;
      let inString = false;
      let escapeNext = false;
      let jsonEnd = -1;

      for (let i = jsonStart; i < buffer.length; i++) {
        const char = buffer[i];

        if (escapeNext) {
          escapeNext = false;
          continue;
        }

        if (char === '\\' && inString) {
          escapeNext = true;
          continue;
        }

        if (char === '"') {
          inString = !inString;
          continue;
        }

        if (!inString) {
          if (char === '{') depth++;
          if (char === '}') {
            depth--;
            if (depth === 0) {
              jsonEnd = i + 1;
              break;
            }
          }
        }
      }

      // Verificar que el tag está completo (termina con ])
      if (jsonEnd !== -1 && jsonEnd < buffer.length && buffer[jsonEnd] === ']') {
        const jsonString = buffer.substring(jsonStart, jsonEnd);
        console.log(`🎯 [TextAnim] Action Tag extraído (${prefix.substring(8, 20)}): ${jsonString.substring(0, 100)}...`);

        try {
          const jsonData = JSON.parse(jsonString);
          console.log(`✅ [TextAnim] JSON parseado correctamente para ${prefix.substring(8, 20)}`);
          handler(jsonData);
          // Remover el tag del buffer
          buffer = buffer.substring(0, startIdx) + buffer.substring(jsonEnd + 1);
          // Buscar la próxima ocurrencia desde el mismo punto
          startIdx = buffer.indexOf(prefix);
        } catch (e) {
          console.error(`❌ [TextAnim] Error parsing JSON para ${prefix}:`, e);
          // Avanzar para evitar loop infinito
          startIdx = buffer.indexOf(prefix, startIdx + 1);
        }
      } else {
        // El tag no está completo, dejarlo en el buffer para el próximo chunk
        console.log(`⏳ [TextAnim] Tag incompleto ${prefix.substring(8, 20)}, esperando más datos...`);
        break;
      }
    }

    return buffer;
  }

  /** Limpia cualquier residuo de action tags que haya quedado */
  private cleanActionResidues(text: string): string {
    if (!text) return text;

    // Patrones de residuos comunes de action tags mal parseados
    // IMPORTANTE: No eliminar espacios normales, solo caracteres de action tags
    let cleaned = text
      .replace(/^\}?\]?(?=\S)/g, '')         // }] al principio solo si hay texto después
      .replace(/(?<=\S)\}?\]?$/g, '')        // }] al final solo si hay texto antes
      .replace(/\[ACTION:[^\]]*$/g, '')      // Action tags incompletos al final
      .replace(/\}\]\s*\[ACTION:/g, '[ACTION:')  // }] entre action tags

    // Eliminar líneas que solo son basura de action tags
    if (/^[\}\]\[\s]+$/.test(cleaned)) {
      return '';
    }

    return cleaned;
  }

  private handleWeatherAction(id: string, weatherData: any, retryCount: number = 0) {
    console.log('🌤️ [TextAnim] handleWeatherAction llamado con:', { id, weatherData, retryCount });
    console.log('🌤️ [TextAnim] Mensajes actuales:', this.dataChat.messages().map((m: any) => ({ id: m.id, role: m.role, hasWeather: !!m.weatherData })));

    const messages = this.dataChat.messages();
    let index = messages.findIndex((m: any) => m.id === id);

    // Si no encontramos el mensaje por ID, buscar el último mensaje de assistant
    if (index === -1) {
      console.log('🌤️ [TextAnim] No se encontró mensaje con id:', id, '- buscando último assistant...');
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          index = i;
          break;
        }
      }
    }

    if (index !== -1) {
      console.log('🌤️ [TextAnim] ✅ Actualizando mensaje en índice:', index);
      this.dataChat.messages.update((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], weatherData };
        return updated;
      });
    } else if (retryCount < 5) {
      // El mensaje aún no existe, reintentar después de un delay
      console.log(`🌤️ [TextAnim] ⏳ Mensaje no encontrado, reintentando en 300ms (intento ${retryCount + 1}/5)...`);
      setTimeout(() => {
        this.handleWeatherAction(id, weatherData, retryCount + 1);
      }, 300);
    } else {
      console.log('🌤️ [TextAnim] ❌ No se encontró ningún mensaje después de 5 reintentos');
    }
  }

  private async handleNavigateToPoiAction(poiData: any) {
    if (!this.isInPlannerContext()) {
      const poiUrl = poiData.poi_url;
      if (!poiUrl) 
        return;
      
      this.router.navigate([poiUrl]);
    } else {
      const poiId = Number(poiData.poi_id);
      const poiType = poiData.poi_type === 'event' ? 'event' : 'point';

      if (!poiId) {
        console.warn('⚠️ [TextAnim] POI sin ID válido para planner, skipping');
        return;
      }

      const item = {
        id: poiId,
        type: poiType
      };

      console.log(`📍 [TextAnim] Planner context → Opening NewDetailsPage for: ${poiData.poi_name} (ID: ${poiId}, type: ${poiType})`);
      this.customRouteService.modalItemInfo(item);
    }

  }

  /** Detecta si estamos en la vista del planner/custom-route */
  private isInPlannerContext(): boolean {
    return this.router.url.includes('custom-route');
  }

  private async handleExternalUrlAction(webData: any) {
    const url = webData.url;
    if (!url) 
      return;

    // Transformar a formato compatible con poiList
    const virtualPoi = {
      poi_id: `web_${btoa(url).substring(0, 16)}`, // ID estable para la URL
      poi_name: webData.title || 'Sitio Web',
      poi_url: url,
      image: null,
      is_fallback_web: true
    };

    this.dataChat.poiList.update(list => {
      const existsIndex = list.findIndex((p: any) => p.poi_url === url);
      if (existsIndex === -1) {
        const newList = [...list, virtualPoi];
        this.dataChat.activePoiIndex.set(newList.length - 1);
        this.dataChat.isPoiSidebarOpen.set(true);
        return newList;
      } else {
        this.dataChat.activePoiIndex.set(existsIndex);
        this.dataChat.isPoiSidebarOpen.set(true);
        return list;
      }
    });
  }

}